import {
  BULLET_CHARS,
  BULLET_PATTERNS_SLATE,
  ORDERED_LIST_PATTERNS_SLATE,
} from '../bulletPatterns.js';

/**
 * Processes Slate fragments to convert numbered paragraphs to ordered lists
 * Creates nested lists as SIBLINGS (not children) to match Volto/Slate's structure
 */
export class SlateListProcessor {
  /**
   * Get nesting level from numbering pattern
   */
  getNestingLevel(text) {
    const match = text.match(/^(\d+\.)+/);
    if (match) {
      const dots = (match[0].match(/\./g) || []).length;
      return dots;
    }
    return 1;
  }

  /**
   * Extract the last number from nested numbering
   */
  extractNumber(text) {
    const numbers = text.match(/\d+/g);
    if (numbers && numbers.length > 0) {
      return parseInt(numbers[numbers.length - 1], 10);
    }
    return null;
  }

  convertNumberedLists(fragment) {
    if (!Array.isArray(fragment)) {
      return fragment;
    }

    const result = [];
    // Stack to track nested lists: [{items: [], level: number, expectedNumber: number}]
    const listStack = [];

    fragment.forEach((node) => {
      if (node && node.type === 'p' && node.children) {
        const firstChild = node.children[0];
        // Match both simple (1.) and nested (1.1., 1.2.1.) numbering
        const isNumberedItem =
          firstChild &&
          firstChild.text &&
          /^(\d+\.)+\s*/.test(firstChild.text.trim());

        if (isNumberedItem) {
          const text = firstChild.text.trim();
          const level = this.getNestingLevel(text);
          const currentNumber = this.extractNumber(text);

          if (currentNumber === null) {
            result.push(node);
            return;
          }

          // Pop lists from stack that are deeper than current level (NOT equal!)
          while (
            listStack.length > 0 &&
            listStack[listStack.length - 1].level > level
          ) {
            const completedList = listStack.pop();
            // Add completed list as SIBLING (not child)
            result.push({
              type: 'ol',
              children: completedList.items,
            });
          }

          // Check if we should start a new list or continue existing
          let targetList = null;

          if (listStack.length > 0) {
            const parentList = listStack[listStack.length - 1];

            // If this is one level deeper and starts at 1, create nested list
            if (level === parentList.level + 1 && currentNumber === 1) {
              targetList = {
                items: [],
                level: level,
                expectedNumber: 1,
              };
              listStack.push(targetList);
            }
            // If same level and sequential, continue parent list
            else if (
              level === parentList.level &&
              currentNumber === parentList.expectedNumber
            ) {
              targetList = parentList;
            }
          }

          // If no target list and this is level 1 starting at 1, create new root list
          if (!targetList && level === 1 && currentNumber === 1) {
            targetList = {
              items: [],
              level: 1,
              expectedNumber: 1,
            };
            listStack.push(targetList);
          }

          // If no target list but level 1 and there's a level 1 in stack, try to continue it
          if (!targetList && level === 1 && listStack.length > 0) {
            const level1 = listStack.find((l) => l.level === 1);
            if (level1 && currentNumber === level1.expectedNumber) {
              targetList = level1;
            }
          }

          if (targetList) {
            // Clean the text by removing the numbering
            let cleanedChildren = [];
            const numberingPattern = /^(\d+\.)+\s*/;

            for (let i = 0; i < node.children.length; i++) {
              const child = node.children[i];
              if (i === 0 && child.text) {
                const cleanedText = child.text
                  .replace(numberingPattern, '')
                  .trim();
                if (cleanedText) {
                  cleanedChildren.push({ ...child, text: cleanedText });
                }
              } else if (child.text && child.text.trim() !== '') {
                cleanedChildren.push({ text: child.text.trim() });
              } else if (child.text === undefined) {
                cleanedChildren.push(child);
              }
            }

            if (cleanedChildren.length === 0) {
              cleanedChildren = [{ text: '' }];
            }

            targetList.items.push({
              type: 'li',
              children: cleanedChildren,
            });
            targetList.expectedNumber++;
          } else {
            result.push(node);
          }
        } else {
          // Not a numbered item - flush all lists
          while (listStack.length > 0) {
            const completedList = listStack.pop();
            result.push({
              type: 'ol',
              children: completedList.items,
            });
          }
          result.push(node);
        }
      } else {
        // Not a paragraph - flush all lists
        while (listStack.length > 0) {
          const completedList = listStack.pop();
          result.push({
            type: 'ol',
            children: completedList.items,
          });
        }
        result.push(node);
      }
    });

    // Flush remaining lists
    while (listStack.length > 0) {
      const completedList = listStack.pop();
      result.push({
        type: 'ol',
        children: completedList.items,
      });
    }

    return result;
  }
}

/**
 * Cleans bullet characters and empty nodes from Slate fragments
 */
export class SlateNodeCleaner {
  cleanSeparateBulletNodes(node) {
    if (
      !node ||
      (node.type !== 'p' && node.type !== 'li') ||
      !node.children ||
      node.children.length < 2
    ) {
      return node;
    }

    const bulletChars = BULLET_CHARS;
    const children = [...node.children];

    if (
      children.length >= 2 &&
      children[0].text !== undefined &&
      children[1].text !== undefined &&
      bulletChars.includes(children[0].text.trim()) &&
      children[1].text.trim() === ''
    ) {
      const cleanedChildren = children.slice(2);

      if (cleanedChildren.length === 0) {
        return null;
      }

      return {
        ...node,
        children: cleanedChildren,
      };
    }

    return node;
  }

  cleanBulletTextFromListItems(node) {
    if (
      !node ||
      node.type !== 'li' ||
      !node.children ||
      node.children.length === 0
    ) {
      return node;
    }

    const bulletPatterns = BULLET_PATTERNS_SLATE;
    const orderedPatterns = ORDERED_LIST_PATTERNS_SLATE;
    const allPatterns = [...bulletPatterns, ...orderedPatterns];

    const cleanedChildren = node.children.map((child, index) => {
      if (child.text !== undefined) {
        let cleanedText = child.text;

        const isFirstTextNode =
          index === 0 ||
          node.children
            .slice(0, index)
            .every(
              (prevChild) =>
                prevChild.text !== undefined && prevChild.text.trim() === '',
            );

        if (isFirstTextNode) {
          for (const pattern of allPatterns) {
            if (pattern.test(cleanedText)) {
              cleanedText = cleanedText.replace(pattern, '');
              break;
            }
          }
        }

        return {
          ...child,
          text: cleanedText,
        };
      }

      return child;
    });

    return {
      ...node,
      children: cleanedChildren,
    };
  }

  filterEmptyNodes(fragment) {
    if (!Array.isArray(fragment)) {
      return fragment;
    }

    return fragment
      .map((node) => {
        if (node && node.type === 'p') {
          return this.cleanSeparateBulletNodes(node);
        }
        if (node && node.type === 'li') {
          const cleanedSeparateNodes = this.cleanSeparateBulletNodes(node);
          if (cleanedSeparateNodes === null) return null;
          return this.cleanBulletTextFromListItems(cleanedSeparateNodes);
        }
        if (
          node &&
          (node.type === 'ul' || node.type === 'ol') &&
          node.children
        ) {
          return {
            ...node,
            children: node.children
              .map((child) => {
                if (child && child.type === 'li') {
                  const cleanedSeparateNodes =
                    this.cleanSeparateBulletNodes(child);
                  if (cleanedSeparateNodes === null) return null;
                  return this.cleanBulletTextFromListItems(
                    cleanedSeparateNodes,
                  );
                }
                return child;
              })
              .filter((child) => child !== null),
          };
        }
        return node;
      })
      .filter((node) => {
        if (node === null) {
          return false;
        }
        if (typeof node === 'object' && node.text !== undefined) {
          const text = node.text.trim();
          if (text === '' || text.replace(/\u00A0/g, '') === '') {
            return false;
          }
          return true;
        }

        if (node && node.type === 'p') {
          if (!node.children || node.children.length === 0) {
            return false;
          }

          const hasContent = node.children.some((child) => {
            if (child.text !== undefined) {
              const text = child.text.trim();
              return text.length > 0 && text.replace(/\u00A0/g, '') !== '';
            }
            if (child.children && Array.isArray(child.children)) {
              return child.children.some((grandchild) => {
                if (grandchild.text !== undefined) {
                  const text = grandchild.text.trim();
                  return text.length > 0 && text.replace(/\u00A0/g, '') !== '';
                }
                return true;
              });
            }
            return true;
          });

          if (!hasContent) {
            return false;
          }
        }

        return true;
      });
  }
}
